import { useEffect, useState } from "react";
import { duration } from "utils/fieldFormat";
import { sgtTime as sgt } from "utils/radarDiagnostics";
import { rawModeText } from "utils/radarStream";

// Pieces shared by the radar viewers on Radar analysis (Live stream, Rich
// wakes) and the Radar rich data card on Node Control.

const REQUEST_STATUS = {
  queued: "being sent",
  sent: "waiting for the node to pick it up",
  acknowledged: "received by the node",
  confirmed: "confirmed by the node",
  not_applied: "not applied: the node reports other values",
  publish_failed: "could not reach the broker",
  superseded: "replaced by a newer request",
  rejected: "rejected by the node",
};
export const requestText = (status) =>
  REQUEST_STATUS[status] || String(status || "sent").replaceAll("_", " ");
export const sentence = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// "Radar stream (…) requested 9 Oct, 11:03:10 SGT · confirmed by the node."
// A request the node did not apply gives the server's reason verbatim, else
// the node's own account of switching the feature off (`switchedOff`, from
// its telemetry), else that it reports other values. A request known only
// from the settings endpoint (`updated`, trackRequest()) has only its last
// update, which the node's later reports move on: once the node has answered
// it, the time is said as that, not as when it was asked ("Rich readings (…)
// · confirmed by the node (last update 10 Oct, 11:21:52 pm SGT)."). `words`:
// the status in other words for this request (a radar_baud request the node
// has received "switches on its next wake").
const ANSWERED = [
  "acknowledged",
  "confirmed",
  "not_applied",
  "rejected",
  "superseded",
];
export function requestTrail(
  what,
  request,
  status,
  switchedOff = null,
  words = null,
) {
  const lastUpdate = !!request.updated && ANSWERED.includes(status);
  const head = lastUpdate
    ? `${sentence(what)} · `
    : `${sentence(what)} requested ${sgt(request.since)} · `;
  const when = lastUpdate ? ` (last update ${sgt(request.since)})` : "";
  if (status === "not_applied")
    return `${head}${
      request.reason
        ? `not applied${when}. ${request.reason}`
        : `not applied${when}: the node reports other values.${switchedOff ? ` It ${switchedOff.text}.` : ""}`
    }`;
  return `${head}${words || requestText(status)}${when}${request.reason ? ` (${request.reason})` : ""}.`;
}

export function Segment({ label, options, value, onChange, disabled = false }) {
  return (
    <div className="field-live-control">
      <span>{label}</span>
      <div className="field-segment" role="group" aria-label={label}>
        {options.map(([v, text]) => (
          <button
            key={v}
            type="button"
            className={value === v ? "active" : ""}
            aria-pressed={value === v}
            disabled={disabled}
            onClick={() => onChange(v)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

// Progressive disclosure on the radar pages: the method, bench and request
// details behind a collapsed summary ("Details", "Why?"), the answer above
// it. Closed unless `open`; it remembers nothing, and warnings, errors and
// requests on their way never go inside one.
export function More({
  label = "Details",
  open = false,
  className = "",
  children,
}) {
  return (
    <details
      className={`field-more${className ? ` ${className}` : ""}`}
      open={open}
    >
      <summary>{label}</summary>
      <div className="field-more-body">{children}</div>
    </details>
  );
}

// A fold's open state: open while `when` holds at first, and opened again
// whenever it comes to hold (a request on its way, an error, a choice that
// is not the default); it never closes by itself, so a fold the user opened
// stays open.
export function useOpenWhen(when) {
  const [open, setOpen] = useState(!!when);
  useEffect(() => {
    if (when) setOpen(true);
  }, [when]);
  return open;
}

export function Fact({ label, children }) {
  return (
    <div className="field-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// "12 s ago · 9 Oct, 11:03:10 SGT", kept current every second.
export function Age({ ms, offset, none = "None in this window" }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  if (!Number.isFinite(ms)) return none;
  const seconds = (now + offset - ms) / 1000;
  return `${seconds < 1.5 ? "just now" : `${duration(seconds)} ago`} · ${sgt(new Date(ms).toISOString())}`;
}

// The experimental raw output mode (firmware .55): one setting for the node,
// used by the stream and by bursts alike.
export function RawModeSelect({
  id,
  value,
  options,
  disabled,
  onChange,
  reported,
  requested,
  children,
}) {
  return (
    <div className="field-stream-mode">
      <label htmlFor={id}>Raw output mode (experimental)</label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map(([m, text]) => (
          <option key={m} value={m}>
            {m} · {text}
          </option>
        ))}
      </select>
      <small className="field-radar-hint">
        {reported !== null
          ? `The node reports mode ${rawModeText(reported)}`
          : "The node has not reported radar_raw_mode yet: firmware rtct.55 or later reports it, and earlier firmware always uses mode 1"}
        {requested !== null && requested !== reported
          ? ` · mode ${requested} requested`
          : ""}
      </small>
      {children}
    </div>
  );
}

// radar_raw_alt (firmware .56): captures take turns in mode 1 (the
// spectrum) and mode 2 (the beat signal), so the stream and bursts bring both
// kinds, one per capture. One setting for the node.
export function RawAltToggle({
  id,
  checked,
  disabled,
  onChange,
  reported,
  requested,
}) {
  return (
    <label className="field-stream-part field-raw-alt" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked ? 1 : 0)}
      />
      <span>
        Alternate spectrum and beat signal
        <small>
          Captures take turns: the spectrum (mode 1), then the beat signal (mode
          2). The mode above applies while this is off.{" "}
          {reported === null
            ? "The node has not reported radar_raw_alt yet (firmware rtct.56 or later)."
            : `The node reports it ${reported ? "on" : "off"}.`}
          {requested !== null && requested !== reported
            ? ` ${requested ? "On" : "Off"} requested.`
            : ""}
        </small>
      </span>
    </label>
  );
}

// Hands the browser a JSON file to save.
export function saveJson(name, body) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(body, null, 2)], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
